import React from "react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { Helmet } from "react-helmet";
import { Calendar, Clock } from "lucide-react";
import { audioTypes, videoTypes } from "../constants/mediaTypeConstants";

const MediaRenderer = ({ src, alt, title }) => {
	const type = src.split(".").pop().toLowerCase();

	if (!type) return null;

	const mediaWrapperClass =
		"relative w-full rounded-lg overflow-hidden bg-gray-100";

	if (audioTypes.includes(type)) {
		return (
			<div className={mediaWrapperClass}>
				<audio controls preload="none" className="w-full">
					<source title={title} src={src} type={`audio/${type}`} />
					Your browser does not support the audio element.
				</audio>
			</div>
		);
	}

	if (videoTypes.includes(type)) {
		const link = src.split(".");
		link.pop();

		return (
			<div className={`${mediaWrapperClass} aspect-w-16 aspect-h-9`}>
				<video
					controls
					playsInline
					webkit-playsinline="true"
					preload="none"
					className="w-full h-full object-cover"
				>
					<source title={title} type="video/webm" />
					<source
						title={title}
						src={`${link.join(".")}.mp4`}
						type="video/mp4"
					/>
					Your browser does not support the video element.
				</video>
			</div>
		);
	}

	return (
		<div className={mediaWrapperClass}>
			<motion.img
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.5 }}
				src={src}
				alt={alt}
				title={alt}
				loading="lazy"
				className="w-full h-auto"
			/>
		</div>
	);
};

export default function MarkdownPost({
	title,
	text,
	image,
	date,
	meta,
	readTime = "5 min read",
}) {
	return (
		<>
			<Helmet>
				<title>{title}</title>
				{meta &&
					Object.entries(meta).map(([key, value]) => (
						<meta key={key} name={key} content={value} />
					))}
			</Helmet>

			<article className="markdown-post max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
				<div className="flex">
					{/* Main Content */}
					<div className="lg:col-span-3">
						{/* Hero Section */}
						<div className="mb-8">
							<motion.h1
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								className="text-4xl font-bold text-gray-900 mb-4"
							>
								{title}
							</motion.h1>

							<div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
								{date && (
									<div className="flex items-center gap-1">
										<Calendar className="w-4 h-4" />
										<time dateTime={date.toISOString()}>
											{date.toLocaleDateString("en-US", {
												weekday: "long",
												year: "numeric",
												month: "long",
												day: "numeric",
											})}
										</time>
									</div>
								)}
								<div className="flex items-center gap-1">
									<Clock className="w-4 h-4" />
									<span>{readTime}</span>
								</div>
							</div>
						</div>

						{/* Featured Image */}
						{image && (
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ duration: 0.5 }}
								className="relative aspect-w-16 aspect-h-9 rounded-xl overflow-hidden mb-8"
							>
								<img
									src={image}
									alt={title}
									className="w-full h-full object-cover"
									loading="lazy"
								/>
							</motion.div>
						)}

						{/* Content */}
						<div className="prose prose-lg prose-gray max-w-none">
							<ReactMarkdown
								components={{
									img: MediaRenderer,
									h1: ({ children }) => (
										<h1
											id={children
												.toString()
												.toLowerCase()
												.replace(/\s+/g, "-")}
										>
											{children}
										</h1>
									),
									h2: ({ children }) => (
										<h2
											id={children
												.toString()
												.toLowerCase()
												.replace(/\s+/g, "-")}
										>
											{children}
										</h2>
									),
									h3: ({ children }) => (
										<h3
											id={children
												.toString()
												.toLowerCase()
												.replace(/\s+/g, "-")}
										>
											{children}
										</h3>
									),
									// Add styling for code blocks
									code: ({ node, inline, className, children, ...props }) => (
										<code
											className={`${className} ${
												inline
													? "bg-gray-100 rounded px-1"
													: "block bg-gray-900 text-white p-4 rounded-lg"
											}`}
											{...props}
										>
											{children}
										</code>
									),
									// Add styling for blockquotes
									blockquote: ({ children }) => (
										<blockquote className="border-l-4 border-primary/20 pl-4 italic">
											{children}
										</blockquote>
									),
								}}
							>
								{text}
							</ReactMarkdown>
						</div>
					</div>
				</div>
			</article>
		</>
	);
}
